import { Box } from "@mui/material";
import Modal from "@mui/material/Modal";
import { addDays } from "date-fns";
import format from "date-fns/format";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import useLang from "Shared/hooks/useLanguage";
import { CrossIcon } from "Shared/Icons";
import { reactSelectSMStyle, styles } from "Utils/Common";
import { DateTimePickerStyling } from "Utils/Common/DateTimePickerStyling";
import { upsertArray } from "Utils/Common/Requisition";
import TimeRange from "react-time-range";


enum FilterTypeColumns {
  Text = "text",
  Datepicker = "datepicker",
  Dropdown = "dropdown",
  MultiSelect = "multiselect",
  Number = "number",
  DateRange = "daterange",
  TimeRange = "timerange", // ✅ NEW
}

function PatientSearchInput({
  column,
  setSearchValue,
  searchValue,
  setFilters,
  filters,
  loadData,
  setCurPage,
}: any) {
  const { t } = useLang();
  const rawFilterType = String(column?.filterColumnsType ?? "")
    .trim()
    .toLowerCase();
  const isNumberFilterType =
    rawFilterType === FilterTypeColumns.Number ||
    rawFilterType.startsWith(`${FilterTypeColumns.Number}:`);
  const numberMaxFromConfig = (() => {
    if (!rawFilterType.startsWith(`${FilterTypeColumns.Number}:`)) {
      return undefined;
    }
    const maxPart = rawFilterType.split(":")[1];
    const parsedMax = Number(maxPart);
    return Number.isFinite(parsedMax) ? parsedMax : undefined;
  })();

  const handleChange = (e: any, name?: string) => {
    console.log(name, "name", e);

    let value;
    let type;
    if (
      name !== FilterTypeColumns.Dropdown &&
      name !== FilterTypeColumns.MultiSelect
    ) {
      value = e.target.value;
      type = e.target.type;
    } else if (name === FilterTypeColumns.MultiSelect) {
      value = (e || []).map((option: any) => option?.value).join(",");
      type = name;
    } else {
      value = e.value;
      type = name;
    }
    if (type === "date") {
      value = moment(value, "YYYY-MM-DD").format("MM/DD/YYYY");
    }
    if (
      isNumberFilterType &&
      type === FilterTypeColumns.Number &&
      value !== ""
    ) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        value = Math.max(0, numericValue);
        if (numberMaxFromConfig !== undefined) {
          value = Math.min(value, numberMaxFromConfig);
        }
      }
    }
    const updatedFilters = [...filters];

    const existingFilterIndex = updatedFilters.findIndex(
      (filter: any) => filter.columnName === column.filterColumns
    );

    if (existingFilterIndex !== -1) {
      updatedFilters[existingFilterIndex] = {
        ...updatedFilters[existingFilterIndex],
        filterValue: value,
      };
    } else {
      updatedFilters.push({
        columnName: column.filterColumns,
        filterValue: value,
        columnType: column.filterColumnsType,
        label: column.columnLabel,
      });
    }

    setFilters(updatedFilters);
    setSearchValue((prevValue: any) => {
      return {
        ...prevValue,
        filters: updatedFilters,
      };
    });
  };

  const getFilterValue = (columnName: any) => {
    const filter = searchValue.filters.find(
      (item: any) => item.columnName === columnName
    );
    if (
      column.filterColumnsType.toLowerCase() === FilterTypeColumns.MultiSelect
    ) {
      const selectedValues = String(filter?.filterValue ?? "")
        .split(",")
        .map((val: string) => val.trim())
        .filter(Boolean);
      return options.filter(
        (opt: any) =>
          selectedValues.includes(String(opt?.value)) ||
          selectedValues.includes(String(opt?.label))
      );
    }
    if (column.filterColumnsType.toLowerCase() === FilterTypeColumns.Dropdown) {
      const option = options.find(
        (opt: any) => opt.value === filter?.filterValue
      );
      return option ? option : null;
    }
    return filter ? filter.filterValue : "";
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      setCurPage && setCurPage(1);
      loadData(false);
    }
  };

  let options: any = [];

  if (column && column.jsonOptionData) {
    try {
      const parsedData = JSON.parse(column.jsonOptionData);
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        options = parsedData;
      }
    } catch (error) {
      console.error(t("Error parsing jsonOptionData:"), error);
    }
  }

  const containsComma = column?.filterColumnsType?.toLowerCase().includes(",");
  const commanSeparatedSecondValue = column?.filterColumnsType?.split(",")[0];
  console.log(column, "column");

  return (
    <div>
      {column?.filterColumnsType?.toLowerCase() === FilterTypeColumns.Text && (
        <input
          id={`PatientDynamicGrid_${column?.columnKey}`}
          type="text"
          className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8 w-100"
          placeholder={t("Search ...")}
          value={getFilterValue(column.filterColumns)}
          name={column?.columnKey}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
      )}
      {isNumberFilterType && (
        <input
          id={`PatientDynamicGrid_${column?.columnKey}`}
          type="number"
          className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8 w-100"
          placeholder={t("Search ...")}
          value={getFilterValue(column.filterColumns)}
          name={column?.columnKey}
          min={0}
          max={numberMaxFromConfig}
          onChange={(e: any) => {
            if (e.target.value === "") {
              handleChange(e);
              return;
            }
            const numericValue = Number(e.target.value);
            if (!Number.isFinite(numericValue)) return;
            let nextValue = Math.max(0, numericValue);
            if (numberMaxFromConfig !== undefined) {
              nextValue = Math.min(nextValue, numberMaxFromConfig);
            }
            handleChange({ ...e, target: { ...e.target, value: nextValue } });
          }}
          onKeyDown={handleKeyPress}
        />
      )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.Dropdown && (
          <Select
            inputId={`PatientDynamicGrid_${column?.columnKey}`}
            menuPortalTarget={document.body}
            placeholder={t("Select ...")}
            theme={(theme) => styles(theme)}
            options={options}
            name={column?.filterColumnsType}
            styles={reactSelectSMStyle}
            value={getFilterValue(column.filterColumns)}
            onChange={(e: any) => handleChange(e, column?.filterColumnsType)}
          />
        )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.MultiSelect && (
          <Select
            inputId={`PatientDynamicGrid_${column?.columnKey}`}
            menuPortalTarget={document.body}
            placeholder={t("Select ...")}
            theme={(theme) => styles(theme)}
            options={options}
            name={column?.filterColumnsType}
            styles={reactSelectSMStyle}
            value={getFilterValue(column.filterColumns)}
            isMulti={true}
            isSearchable={true}
            onChange={(e: any) => handleChange(e, FilterTypeColumns.MultiSelect)}
          />
        )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.Datepicker && (
          <SearchDatePicker
            value={getFilterValue(column.filterColumns)}
            onChange={handleChange}
          />
        )}
      {containsComma &&
        commanSeparatedSecondValue.toLowerCase() ===
        FilterTypeColumns.DateRange && (
          <DateRange
            column={column}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.DateRange && (
          <DateRange
            column={column}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        )}

      {/* ✅ NEW: TimeRange condition */}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.TimeRange && (
          <TimeRangePicker
            column={column}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filters={filters}
            setFilters={setFilters}
          />
        )}
    </div>
  );
}

export default PatientSearchInput;

const DateRange = ({ column, searchValue, setSearchValue }: any) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const handleClose = () => {
    setOpen(false);
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
  };

  const refOne = useRef(null);

  useEffect(() => {
    document.addEventListener("keydown", hideOnEscape, true);
  }, []);

  const hideOnEscape = (e: any) => {
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const orientation = window.matchMedia("(max-width: 700px)").matches
    ? "vertical"
    : "horizontal";

  const setDateToInput = (searchValue: any, columnKey?: string) => {
    let filterDate = searchValue?.filters?.find(
      (filteredObj: any) => filteredObj.columnName == columnKey
    );

    if (filterDate?.columnName == columnKey) {
      return filterDate?.filterValue;
    } else {
      return "";
    }
  };

  return (
    <div>
      <input
        id={`PatientDynamicGrid_${column?.columnKey}`}
        value={setDateToInput(searchValue, column?.columnKey)}
        readOnly
        onClick={() => {
          setOpen(!open);
        }}
        className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8"
        placeholder={t("MM/DD/YYYY")}
      />

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={DateTimePickerStyling}>
          <div ref={refOne}>
            {open && (
              <>
                <Box onClick={handleClose} className="icon-container">
                  <CrossIcon />
                </Box>
                <DateRangePicker
                  onChange={(item: any) => {
                    setRange([item.selection]);
                    let startDate = format(
                      item?.selection?.startDate,
                      "MM/dd/yyyy"
                    );
                    let endDate = format(
                      item?.selection?.endDate,
                      "MM/dd/yyyy"
                    );
                    console.log();

                    let concatedDate = startDate + " to " + endDate;
                    setSearchValue((preVal: any) => {
                      return {
                        ...preVal,
                        [column.columnKey]: concatedDate,
                      };
                    });
                    let filterObj = {
                      columnName: column.columnKey,
                      filterValue: concatedDate,
                      columnType: column.filterColumnsType,
                    };
                    searchValue.filters = upsertArray(
                      searchValue.filters,
                      filterObj,
                      (element: any) =>
                        element.columnName === filterObj.columnName
                    );
                  }}
                  editableDateInputs={true}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                  initialFocusedRange={[] as any}
                  months={2}
                  direction="horizontal"
                  maxDate={addDays(new Date(), 0)}
                />
              </>
            )}
          </div>
          <div style={{ textAlign: "end", padding: "0% 2%" }}>
            <button onClick={handleClose} className="btn btn-primary btn-sm">
              {t("Submit")}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

// ✅ NEW: TimeRangePicker Component
const TimeRangePicker = ({
  column,
  searchValue,
  setSearchValue,
  filters,
  setFilters,
}: any) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState({
    startTime: moment(),
    endTime: moment(),
  });
  const refTimePicker = useRef(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const style = {
    position: "absolute" as const,
    top: "20%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  };

  const handleChangeStartTime = (event: any) => {
    const startTime = moment(event.startTime)
      .local()
      .format("YYYY-MM-DD HH:mm:ss");
    let concatedTime = "";

    if (typeof time.endTime === "string") {
      concatedTime =
        startTime +
        " to " +
        moment(time.endTime).local().format("YYYY-MM-DD HH:mm:ss");
    } else {
      concatedTime = startTime + " to " + startTime;
    }

    setTime((preVal) => ({
      ...preVal,
      startTime: event.startTime,
    }));

    updateSearchAndFilters(concatedTime);
  };

  const handleChangeEndTime = (event: any) => {
    const endTime = moment(event.endTime).local().format("YYYY-MM-DD HH:mm:ss");
    let concatedTime = "";

    if (typeof time.startTime === "string") {
      concatedTime =
        moment(time.startTime).local().format("YYYY-MM-DD HH:mm:ss") +
        " to " +
        endTime;
    } else {
      concatedTime = endTime + " to " + endTime;
    }

    setTime((preVal) => ({
      ...preVal,
      endTime: event.endTime,
    }));

    updateSearchAndFilters(concatedTime);
  };

  const updateSearchAndFilters = (concatedTime: string) => {
    setSearchValue((preVal: any) => ({
      ...preVal,
      [column?.columnKey]: concatedTime,
    }));

    const filterObj = {
      columnName: column.filterColumns,
      filterValue: concatedTime,
      columnType: column.filterColumnsType,
      label: column.columnLabel,
    };

    const updatedFilters = upsertArray(
      filters || [],
      filterObj,
      (element: any) => element.columnName === filterObj.columnName
    );

    setFilters(updatedFilters);
  };

  const handleSubmit = () => {
    const { startTime, endTime } = time;

    if (!startTime || !endTime || moment(startTime).isAfter(endTime)) {
      console.error("Invalid time range selected");
      return;
    }

    const formattedStartTime = moment(startTime)
      .local()
      .format("YYYY-MM-DD HH:mm:ss");
    const formattedEndTime = moment(endTime)
      .local()
      .format("YYYY-MM-DD HH:mm:ss");
    const concatenatedTime = `${formattedStartTime} to ${formattedEndTime}`;

    setSearchValue((prevValue: any) => ({
      ...prevValue,
      [column?.columnKey]: concatenatedTime,
    }));

    const filterObj = {
      columnName: column?.filterColumns,
      filterValue: concatenatedTime,
      columnType: column?.filterColumnsType,
      label: column?.columnLabel,
    };

    const updatedFilters = upsertArray(
      filters || [],
      filterObj,
      (element: any) => element.columnName === filterObj.columnName
    );

    setFilters(updatedFilters);
    setSearchValue((prevValue: any) => ({
      ...prevValue,
      filters: updatedFilters,
    }));

    handleClose();
  };

  const getDisplayValue = () => {
    const filter = searchValue?.filters?.find(
      (item: any) => item.columnName === column?.filterColumns
    );
    if (filter?.filterValue) {
      return (
        moment(time.startTime).format("HH:mm") +
        " - " +
        moment(time.endTime).format("HH:mm")
      );
    }
    return "";
  };

  return (
    <div>
      <input
        id={`PatientDynamicGrid_${column?.columnKey}`}
        onClick={handleOpen}
        readOnly
        className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8"
        placeholder={t("00:00 - 00:00")}
        value={getDisplayValue()}
        style={{ cursor: "pointer" }}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="time-range-modal"
        aria-describedby="time-range-picker-modal"
      >
        <Box sx={style}>
          <div ref={refTimePicker}>
            {open && (
              <>
                <Box className="icon-container">
                  <span onClick={handleClose} className="cursor-pointer">
                    <CrossIcon />
                  </span>
                </Box>
                <TimeRange
                  use24Hours={true}
                  onStartTimeChange={handleChangeStartTime}
                  onEndTimeChange={handleChangeEndTime}
                  startMoment={time.startTime}
                  endMoment={time.endTime}
                />
              </>
            )}
          </div>
          <div style={{ textAlign: "end", padding: "0% 2%" }}>
            <button
              onClick={handleSubmit}
              className="btn btn-sm fw-500 btn-primary"
            >
              {t("Submit")}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};
